import { useRouter } from 'next/router'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Separator } from '@wartek-id/separator'
import { Button } from '@wartek-id/button-v1'
import { Shimmer } from '@wartek-id/shimmer'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'
import { Spinner } from '@wartek-id/spinner'

import { Header } from 'app/KinerjaShared/components/Header'
import { usePelaksanaanPengembanganKompetensiPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPengembanganKompetensiPS'
import ListKegiatan from './ListKegiatan'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'

const MAX_LENGTH_INPUT = 250

const MSG_ERR_REQUIRED = 'Wajib diisi.'
const MSG_ERR_MAX_LENGTH = `Maksimal ${MAX_LENGTH_INPUT} karakter.`
const MSG_ERR_SUBMIT = 'Gagal simpan dokumen. Coba lagi.'
const MSG_SUCCESS_SUBMIT = 'Berhasil simpan dokumen!'

const PSPelaksanaanPengembanganKompetensiPage = () => {
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = usePPSTrackEvent()

  const { pelaksanaan, submitPelaksanaan, apiStatus, apiStatusSubmit } =
    usePelaksanaanPengembanganKompetensiPS()

  const [isOpenDialogKegiatan, setIsOpenDialogKegiatan] = useState(false)
  const [isShowForm, setIsShowForm] = useState(false)
  const { register, setValue, watch, formState, handleSubmit } = useForm({
    defaultValues: {
      pengembanganKompetensiPelaksanaanInspirasi: '',
      pengembanganKompetensiPelaksanaanPenerapanInspirasi: '',
    },
  })

  const {
    pengembanganKompetensiPelaksanaanInspirasi,
    pengembanganKompetensiPelaksanaanPenerapanInspirasi,
  } = watch()

  const submitForm = async () => {
    try {
      await submitPelaksanaan({
        pengembanganKompetensiPelaksanaanInspirasi,
        pengembanganKompetensiPelaksanaanPenerapanInspirasi,
      })

      trackEvent('pelaksanaan pegawai', 'submit pengembangan kompetensi')

      toast({
        message: MSG_SUCCESS_SUBMIT,
        type: 'success',
      })
      setTimeout(() => {
        router.push('/pengelolaan-kinerja/pengawas-sekolah/pegawai')
      }, 1000)
    } catch (error) {
      toast({
        message: MSG_ERR_SUBMIT,
      })
    }
  }

  useEffect(() => {
    setValue(
      'pengembanganKompetensiPelaksanaanInspirasi',
      pelaksanaan?.pengembanganKompetensiPelaksanaanInspirasi ?? ''
    )
    setValue(
      'pengembanganKompetensiPelaksanaanPenerapanInspirasi',
      pelaksanaan?.pengembanganKompetensiPelaksanaanPenerapanInspirasi ?? ''
    )
    setIsShowForm(
      !pelaksanaan?.isEditable ||
        !!pelaksanaan?.pengembanganKompetensiPelaksanaanInspirasi ||
        !!pelaksanaan?.pengembanganKompetensiPelaksanaanPenerapanInspirasi
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pelaksanaan])

  return (
    <>
      <div className="bg-white py-9">
        <div className="max-w-7xl mx-auto">
          <Header
            title="Pengembangan Kompetensi"
            className="print:hidden"
            onBack={router.back}
          />
        </div>
      </div>

      <div className="pt-10 pb-32 max-w-7xl mx-auto">
        <div className="flex gap-3">
          <div className="flex flex-col gap-3 flex-1 max-w-96">
            <div className="bg-white rounded border border-subdued p-4">
              <Text variant="heading-sm">Indikator pilihan Anda</Text>

              <Separator className="my-2" />

              <Text color="subdued">
                {apiStatus === 'loading' && <Shimmer className="!w-full" />}
                {pelaksanaan?.pengembanganKompetensiIndikator.deskripsi}
              </Text>
            </div>

            <div
              className="bg-white rounded border border-subdued p-4"
              data-testid="section-kegiatan"
            >
              <Text variant="heading-sm">
                Kegiatan Anda (
                {pelaksanaan?.pengembanganKompetensiKegiatanValues.length})
              </Text>

              <Separator className="my-4" />

              {apiStatus === 'loading' && <Shimmer className="!w-full" />}
              <ListKegiatan
                pengembanganKompetensiKegiatanValues={
                  pelaksanaan?.pengembanganKompetensiKegiatanValues ?? []
                }
                limit={1}
              />

              <Button
                size="sm"
                variant="ghost"
                className="mt-4"
                onClick={() => setIsOpenDialogKegiatan(true)}
              >
                Tampilkan semua kegiatan Anda
                <Icon fontSize="sm" color="informational" className="ml-1">
                  chevron_right
                </Icon>
              </Button>
            </div>

            <div className="rounded border border-subdued p-4">
              <div className="flex gap-1">
                <Icon>info</Icon>
                <Text>
                  <strong>Tidak perlu kumpulkan Bukti Dukung</strong> untuk
                  Pengembangan Kompetensi.
                </Text>
              </div>
            </div>
          </div>

          <div className="bg-white rounded border border-subdued flex-1 self-start">
            <div className="bg-surface-selected-default p-6 rounded-t">
              <Text variant="heading-lg">Isi Dokumen Refleksi</Text>
              <Text className="mt-1">
                Anda dapat ubah pengisian secara berkala selama Penetapan
                Predikat Kinerja belum dilakukan.
              </Text>
            </div>

            <div className="p-6">
              {apiStatus === 'loading' ? (
                <div className="flex justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  {isShowForm ? (
                    <form onSubmit={handleSubmit(submitForm)}>
                      {!pelaksanaan?.isEditable && (
                        <div className="flex gap-2 mb-6">
                          <Icon color="informational">info</Icon>
                          <Text className="!text-blue-60">
                            Isi tidak dapat diubah lagi karena Penetapan
                            Predikat Kinerja sudah dilakukan.
                          </Text>
                        </div>
                      )}

                      <div className="flex flex-col gap-1">
                        <Text>
                          <strong>
                            Apa inspirasi menarik yang Anda peroleh dari
                            kegiatan?
                          </strong>
                          <br />
                          Ketik maksimal 3 inspirasi:
                        </Text>
                        <Textarea
                          placeholder="Ketik di sini"
                          {...register(
                            'pengembanganKompetensiPelaksanaanInspirasi',
                            {
                              required: {
                                value: true,
                                message: MSG_ERR_REQUIRED,
                              },
                              maxLength: {
                                value: MAX_LENGTH_INPUT,
                                message: MSG_ERR_MAX_LENGTH,
                              },
                            }
                          )}
                          isDisabled={!pelaksanaan?.isEditable}
                          isInvalid={
                            !!formState.errors
                              .pengembanganKompetensiPelaksanaanInspirasi
                              ?.message
                          }
                          rows={3}
                          data-testid="field-pengembanganKompetensiPelaksanaanInspirasi"
                        />
                        <div className="flex justify-between">
                          <Text
                            variant="helper"
                            color="critical"
                            className="text-right"
                          >
                            {
                              formState.errors
                                .pengembanganKompetensiPelaksanaanInspirasi
                                ?.message
                            }
                          </Text>

                          <Text
                            variant="helper"
                            color="subdued"
                            className="text-right"
                          >
                            {pengembanganKompetensiPelaksanaanInspirasi.length}/
                            {MAX_LENGTH_INPUT}
                          </Text>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1 mt-3">
                        <Text>
                          <strong>
                            Bagaimana Anda menerapkan inspirasi tersebut untuk
                            kemajuan penguasaan kompetensi?
                          </strong>
                          <br />
                          Ketik minimal 1 penerapan untuk kemajuan penguasaan
                          kompetensi Anda:
                        </Text>
                        <Textarea
                          placeholder="Ketik di sini"
                          {...register(
                            'pengembanganKompetensiPelaksanaanPenerapanInspirasi',
                            {
                              required: {
                                value: true,
                                message: MSG_ERR_REQUIRED,
                              },
                              maxLength: {
                                value: MAX_LENGTH_INPUT,
                                message: MSG_ERR_MAX_LENGTH,
                              },
                            }
                          )}
                          isDisabled={!pelaksanaan?.isEditable}
                          isInvalid={
                            !!formState.errors
                              .pengembanganKompetensiPelaksanaanPenerapanInspirasi
                              ?.message
                          }
                          rows={3}
                          data-testid="field-pengembanganKompetensiPelaksanaanPenerapanInspirasi"
                        />
                        <div className="flex justify-between">
                          <Text
                            variant="helper"
                            color="critical"
                            className="text-right"
                          >
                            {
                              formState.errors
                                .pengembanganKompetensiPelaksanaanPenerapanInspirasi
                                ?.message
                            }
                          </Text>

                          <Text
                            variant="helper"
                            color="subdued"
                            className="text-right"
                          >
                            {
                              pengembanganKompetensiPelaksanaanPenerapanInspirasi.length
                            }
                            /{MAX_LENGTH_INPUT}
                          </Text>
                        </div>
                      </div>

                      <div className="fixed bottom-0 left-0 right-0 z-10 flex justify-center w-full p-4 bg-white border-t border-subdued">
                        <Button
                          type="submit"
                          disabled={!pelaksanaan?.isEditable}
                          isLoading={apiStatusSubmit === 'loading'}
                        >
                          Simpan
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="py-4 flex gap-6 items-center justify-center">
                      <Image
                        alt=""
                        src="/images/pengelolaan-kinerja/img-dokumen-refleksi.png"
                        width={287}
                        height={169}
                      />
                      <div className="max-w-72">
                        <Text variant="action">
                          Ceritakan inspirasi dan kemajuan penguasaan kompetensi
                          Anda.
                        </Text>
                        <Button
                          size="lg"
                          className="mt-3"
                          data-testid="btn-fill-form"
                          onClick={() => {
                            setIsShowForm(true)
                            trackEvent(
                              'pelaksanaan pegawai',
                              'start pengembangan kompetensi'
                            )
                          }}
                        >
                          Isi Dokumen Refleksi
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <Dialog
          size="md"
          isOpen={isOpenDialogKegiatan}
          onClose={() => setIsOpenDialogKegiatan(false)}
        >
          <DialogOverlay />
          <DialogContent>
            <DialogCloseButton />
            <DialogHeader>
              <Text variant="heading-md">
                Kegiatan (
                {pelaksanaan?.pengembanganKompetensiKegiatanValues.length})
              </Text>
            </DialogHeader>
            <div className="p-6" data-testid="dialog-kegiatan-content">
              <ListKegiatan
                pengembanganKompetensiKegiatanValues={
                  pelaksanaan?.pengembanganKompetensiKegiatanValues ?? []
                }
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  )
}

export default PSPelaksanaanPengembanganKompetensiPage
